/**
 * THE STYLE RECORD — one vocabulary for every style source in Deck.
 *
 * WHY THIS EXISTS. An audit (2026-08-12) traced all six style-carrying entities — brand kit, design
 * system, website scrape, reference image, moodboard, artifact theme — and found they already converge
 * on two shapes in `utils/moodboardStyle.ts`. The convergence was never named, so it drifted:
 *
 *   - `spacing`, `shadows` and `patterns` are ENGLISH SENTENCES. The model re-interprets them on every
 *     generation, which is where run-to-run inconsistency comes from.
 *   - `BrandStyle.spacing` (a real string[] the backend works to extract) is read by NOTHING.
 *   - Artifacts speak `--a-bg / --a-text / --a-accent`; brand kits speak `roles.background /
 *     roles.text / roles.accent`. Same concepts, zero translation. A brand kit cannot reach an artifact.
 *   - `MoodboardProfile.typography` (a sentence) and `BrandStyleTokens.typography` (a Record of CSS
 *     facts) are different things wearing the same name.
 *
 * THE RULE THIS FILE ENCODES: **every source is an importer, never a format.** A website scrape, a
 * vision synthesis, a Figma pull, a hand edit — all write a `Style`. Everything downstream reads a
 * `Style` and nothing else. Adding a seventh source must not touch a renderer; adding a renderer must
 * not touch an importer.
 *
 * SCOPE, DELIBERATE. This layer is ADDITIVE. It does not replace the live serializers, because the
 * canvas works today and a big-bang swap would be untestable. `Style` earns its way in through the
 * playground (`/style-lab`), where the same source is generated across models and scored. Flipping
 * generation onto it is a separate, small change made on evidence.
 *
 * @see styleImport.ts   sources  → Style
 * @see styleRender.ts   Style    → prompt block / CSS custom properties
 * @see STYLE_SEMANTICS.md
 */

// TYPE-ONLY, and the only import this file has. `typeScale.ts` is a leaf (it imports nothing), so even a
// runtime edge would cost nothing, but the record is the contract every importer and renderer reads and it
// should stay visibly dependency-free. The five roles are DEFINED there because the kit panel already edits
// that shape; defining a second copy here is how two type systems drift apart.
import type { TypeScale } from './typeScale'

/** Where a field came from. Precedence used to be scattered across if-branches in three generation
 *  hooks; making the origin a value turns "who wins" into data a test can assert on. */
export type StyleSource =
  | 'website' // CSS scraped from a real page — the only source that MEASURES rather than guesses
  | 'image' // one reference image read by a vision model
  | 'moodboard' // several images read together
  | 'brandKit' // a saved kit the user curates
  | 'designSystem' // a canvas-local kit
  | 'figma' // Figma variables / styles
  | 'manual' // typed by a person
  | 'preset' // a taste default we shipped

/**
 * How much to trust a field. This is the field the audit was missing: today an LLM's colour guess and a
 * hex read out of a stylesheet are the same `string`, so the guess can silently overwrite the fact.
 *
 * - `measured`  read from a real artifact (CSS declaration, pixel histogram, Figma variable)
 * - `inferred`  a model looked at something and decided
 * - `default`   we filled it in because the source was silent
 */
export type StyleConfidence = 'measured' | 'inferred' | 'default'

export interface StyleProvenance {
  from: StyleSource
  confidence: StyleConfidence
  /** The page / file / kit this came from, for the playground's "why does it look like this" panel. */
  origin?: string
}

/**
 * The visual structure that carries a slide's argument.
 *
 * These names are NOT invented here — they are the vocabulary Deck's own slide judgment already uses
 * (`app/lib/deckJudgment.server.ts`: "a split, a spectrum, a field, an orbit, a ledger, a single
 * number"). Reusing them means the thing that PICKS a layout and the thing that SCORES one speak the
 * same language, which is the whole point of a shared semantic.
 *
 * An enum, not prose, precisely so that "did the model use the layout the style asked for?" is a
 * checkable question rather than a matter of opinion.
 */
export type LayoutPattern =
  | 'split' // two halves in tension
  | 'spectrum' // an ordered range
  | 'field' // scattered items in a bounded space
  | 'orbit' // a centre with satellites
  | 'ledger' // aligned rows of fact
  | 'single-number' // one figure, given the whole canvas
  | 'stack' // vertical rhythm, no columns
  | 'grid' // regular repeating cells
  | 'full-bleed' // image or colour to every edge

/** The union as a runtime set, so a value from a manifest or a model can be checked, not cast. Exhaustive
 *  in both directions: a member missing here fails to compile. */
export const LAYOUT_PATTERNS: Record<LayoutPattern, true> = {
  split: true,
  spectrum: true,
  field: true,
  orbit: true,
  ledger: true,
  'single-number': true,
  stack: true,
  grid: true,
  'full-bleed': true,
}

export function isLayoutPattern(value: unknown): value is LayoutPattern {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(LAYOUT_PATTERNS, value)
}

/** 'Single Number' / 'full bleed' / ' Orbit ' → the union member; anything else undefined. Case and
 *  separators only, no aliasing: a model that wrote "table" meant something this vocabulary does not name,
 *  and guessing a family for it would be a fabricated record. */
export function asLayoutPattern(raw: unknown): LayoutPattern | undefined {
  if (typeof raw !== 'string') return undefined
  const key = raw.trim().toLowerCase().replace(/[\s_]+/g, '-')
  return isLayoutPattern(key) ? key : undefined
}

/** Perceived density. A word, but a CLOSED word — three values a renderer can switch on. */
export type StyleDensity = 'tight' | 'normal' | 'airy'

/** Shadow language. `none` is a real design position, not a missing value. */
export type StyleDepth = 'none' | 'soft' | 'hard'

/** A real font file the source shipped, so generated slides can load the actual typeface. */
export interface StyleFontFace {
  family: string
  url: string
  weight?: string
  style?: string
}

/** Semantic colour roles. Named after what they DO, not where they sit, so a dark source and a light
 *  source produce the same field names. `palette` keeps the raw ordered list for swatches and for any
 *  role we could not assign. */
export interface StyleColor {
  bg?: string
  surface?: string
  text?: string
  muted?: string
  accent?: string
  border?: string
  /** Most prominent first. Always hex, always 6-digit — importers normalise. */
  palette: string[]
  /**
   * EVERY ground this brand paints on, `bg` first. Guri, 2026-09-05: a blue cover and a yellow cover
   * from one kit were not expressible, because `projectColorGroups` takes the first background and
   * flattens the rest into `palette`, where a ground is indistinguishable from a swatch. This is the
   * list the variants of a role are keyed on (Phase C), and the recipe reads it today to tell the
   * document that other grounds exist and that it sits on exactly one of them.
   */
  grounds?: string[]
}

export interface StyleType {
  /** Family name for headings. */
  display?: string
  /** Family name for body copy. */
  body?: string
  /** Weights the display face ACTUALLY ships. Asking for a weight outside this list makes the browser
   *  synthesize fake bold, which wrecks display letterforms — a real bug this field prevents. */
  displayWeights?: string[]
  /** Ratio between steps of the type scale (1.25 = major third). A number, so it can be applied. */
  scale?: number
  /**
   * THE FIVE SIZES, as the kit's own type roles (display, heading, subheading, body, caption), each with a
   * face, a px size at 1920x1080, a weight and a usage rule.
   *
   * Sizes live on the record because the recipe is the artifact's only door and, measured on 2026-09-05,
   * `## Type` emitted families and weights and never a px: the sizes rode a separate block appended AFTER
   * the budget fit, gated on a scale being STORED, and were cut mid-sentence on a rich kit. A field here
   * is claimed and merged like every other field, so a wired site's measured scale beats a kit's default
   * one and the renderer states five numbers instead of a ratio.
   */
  roles?: TypeScale
  /** Display letter-spacing in em. Sites tune tracking for 16px body text; at 120px it crushes. */
  tracking?: number
  /** Display line-height as a multiplier. */
  leading?: number
  case?: 'none' | 'upper'
}

export interface StyleSpace {
  /**
   * The WORKING RHYTHM in px — the gap the page actually breathes at — not the atomic divisor of its
   * spacing ramp. This field replaces "Generous whitespace, roughly 40px between blocks" as a sentence.
   *
   * The distinction is load-bearing and has already been got wrong once. A ramp of [4, 12, 16, 24] has a
   * greatest common divisor of 4, and 4 is what 12 and 16 are BUILT from — but a deck told its base step is
   * 4px lays out cramped, which is exactly the complaint that started this work. 12 is the number a
   * designer would name. Importers therefore take the dominant step that explains most of the ramp, and
   * treat sub-8px values as hairlines unless the whole ramp is small. Anyone tempted to "correct" this to a
   * gcd should read `readSpacingRhythm` first, where the same temptation is recorded.
   */
  unit?: number
  density?: StyleDensity
}

export interface StyleRadius {
  sm?: number
  md?: number
  lg?: number
}

export interface StyleShadow {
  depth?: StyleDepth
  /** Literal CSS box-shadow values, when a source actually measured them. */
  values?: string[]
}

export interface StyleLayout {
  /** Which structures this style reaches for, best first. Empty = no opinion, generator chooses. */
  patterns: LayoutPattern[]
}

/**
 * The prose half, kept ON PURPOSE.
 *
 * The fix for "half our semantics are English" is not to delete the English — models are genuinely good
 * at tone and at what to avoid, and no token captures "confident, plain, no hype". The fix is to stop
 * expressing MEASURABLE things as prose. Anything a designer would put a number on lives above; what
 * survives here is only what has no unit.
 */
/** One written line that SHOWS the voice. `slot` is the kind of copy it is an example of — free text here
 *  rather than the profile's closed union, because a renderer only ever prints it. */
export interface StyleVoiceExample {
  kind: 'do' | 'dont' | 'sample'
  slot?: string
  text: string
  note?: string
}

export interface StyleVoice {
  tone?: string
  keywords: string[]
  avoids: string[]
  /**
   * STANDING RULES — what the user told us once and expects to hold on every later document.
   *
   * Separate from `guideline` and separate from `layout.patterns`, and the second separation is the
   * load-bearing one. `MoodboardProfile.patterns` goes through `matchLayoutPatterns`, whose keyword table
   * holds `stack`, `grid`, `tiles`, `rows of` and `one number`, so "never stack two adjectives in a
   * headline" carried through that field arrives as a request for the STACK layout family — the user's own
   * fix, inverted. Rules therefore enter here directly and are rendered as their own recipe section.
   *
   * Optional rather than a required array, unlike `keywords` and `avoids`, because half a dozen call sites
   * outside this folder build a `StyleVoice` literal; readers default with `?? []`.
   */
  rules?: string[]
  /** Lines that demonstrate the voice instead of describing it. A model matches a sample far more
   *  reliably than it matches an adjective. */
  examples?: StyleVoiceExample[]
  /** Free art direction. The escape hatch — but it must never be the only place a token lives. */
  guideline?: string
}

export interface StyleAssets {
  logoUrl?: string
  /** True when the mark reads on a light background — drives contrast placement at bake time. */
  logoOnLight?: boolean
  fontFaces?: StyleFontFace[]
}

/** Dotted paths that may carry provenance. Kept explicit so a typo is a compile error. */
export type StyleField =
  | 'color.bg'
  | 'color.surface'
  | 'color.text'
  | 'color.muted'
  | 'color.accent'
  | 'color.border'
  | 'color.palette'
  | 'color.grounds'
  | 'type.display'
  | 'type.body'
  | 'type.displayWeights'
  | 'type.scale'
  | 'type.roles'
  | 'type.tracking'
  | 'type.leading'
  | 'type.case'
  | 'space.unit'
  | 'space.density'
  | 'radius'
  | 'shadow'
  | 'layout.patterns'
  | 'voice'
  | 'assets.logoUrl'
  | 'assets.fontFaces'

/**
 * ONE style. Every source imports into this; every consumer renders from this.
 *
 * Every category is optional because a real source is always partial — a website gives colour and type
 * but never layout patterns, a moodboard gives mood but rarely a radius. Absence is information: it is
 * what a preset is allowed to fill.
 */
export interface Style {
  id: string
  name: string
  color: StyleColor
  type: StyleType
  space: StyleSpace
  radius: StyleRadius
  shadow: StyleShadow
  layout: StyleLayout
  voice: StyleVoice
  assets: StyleAssets
  provenance: Partial<Record<StyleField, StyleProvenance>>
}

/** An empty Style. Importers start here and fill what their source actually knows, so no importer has
 *  to remember the full field list — and adding a field to `Style` cannot silently produce `undefined`
 *  where a renderer expected an array. */
export function emptyStyle(id: string, name: string): Style {
  return {
    id,
    name,
    color: { palette: [] },
    type: {},
    space: {},
    radius: {},
    shadow: {},
    layout: { patterns: [] },
    voice: { keywords: [], avoids: [] },
    assets: {},
    provenance: {},
  }
}

/** 6-digit lowercase hex, or undefined. Every importer runs colours through this, so downstream code
 *  never has to cope with `#FFF`, `rgb(0 0 0)` or a stray CSS variable name. */
export function normalizeHex(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined
  const s = raw.trim().toLowerCase()
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(s)
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`
  if (/^#[0-9a-f]{6}$/.test(s)) return s
  const rgb = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/.exec(s)
  if (rgb) {
    const hex = (n: string) => Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, '0')
    return `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}`
  }
  return undefined
}

/** A deep copy of a type scale. `TypeStyle` is flat, so one level of spread per role is the whole copy. */
function cloneScale(scale: TypeScale): TypeScale {
  const out = {} as TypeScale
  for (const role of Object.keys(scale) as (keyof TypeScale)[]) out[role] = { ...scale[role] }
  return out
}

/**
 * MERGE, by confidence rather than by call order.
 *
 * The audit found the live precedence rules contradict each other: a wired style IMAGE beats a wired
 * WEBSITE on the per-slide path, even though the website's hexes were read from a stylesheet and the
 * image's were guessed by a vision model. Here `measured` beats `inferred` beats `default`, whatever
 * order the sources arrive in. Ties go to the later argument, so an explicit user edit still wins.
 */
export function mergeStyles(base: Style, incoming: Style): Style {
  const rank: Record<StyleConfidence, number> = { default: 0, inferred: 1, measured: 2 }
  const out: Style = {
    ...base,
    id: incoming.id,
    name: incoming.name || base.name,
    color: { ...base.color },
    type: { ...base.type },
    space: { ...base.space },
    radius: { ...base.radius },
    shadow: { ...base.shadow },
    layout: { patterns: [...base.layout.patterns] },
    voice: { ...base.voice },
    assets: { ...base.assets },
    provenance: { ...base.provenance },
  }

  /** True when `incoming` is allowed to overwrite this field. Unclaimed fields always lose to a claim. */
  const wins = (field: StyleField): boolean => {
    const a = base.provenance[field]
    const b = incoming.provenance[field]
    if (!b) return false
    if (!a) return true
    return rank[b.confidence] >= rank[a.confidence]
  }

  const claim = (field: StyleField, apply: () => void) => {
    if (!wins(field)) return
    apply()
    out.provenance[field] = incoming.provenance[field]
  }

  claim('color.bg', () => (out.color.bg = incoming.color.bg))
  claim('color.surface', () => (out.color.surface = incoming.color.surface))
  claim('color.text', () => (out.color.text = incoming.color.text))
  claim('color.muted', () => (out.color.muted = incoming.color.muted))
  claim('color.accent', () => (out.color.accent = incoming.color.accent))
  claim('color.border', () => (out.color.border = incoming.color.border))
  claim('color.palette', () => (out.color.palette = [...incoming.color.palette]))
  claim('color.grounds', () => (out.color.grounds = incoming.color.grounds ? [...incoming.color.grounds] : undefined))
  claim('type.display', () => (out.type.display = incoming.type.display))
  claim('type.body', () => (out.type.body = incoming.type.body))
  claim('type.displayWeights', () => (out.type.displayWeights = incoming.type.displayWeights))
  claim('type.scale', () => (out.type.scale = incoming.type.scale))
  // WHOLE, never per role. A scale is one decision about hierarchy: a display size from a site and a body
  // size from a kit is a ramp nobody designed, so the claim moves all five roles together, deep-copied so
  // a later hand edit on one record cannot reach into the other.
  claim('type.roles', () => (out.type.roles = incoming.type.roles ? cloneScale(incoming.type.roles) : undefined))
  claim('type.tracking', () => (out.type.tracking = incoming.type.tracking))
  claim('type.leading', () => (out.type.leading = incoming.type.leading))
  claim('type.case', () => (out.type.case = incoming.type.case))
  claim('space.unit', () => (out.space.unit = incoming.space.unit))
  claim('space.density', () => (out.space.density = incoming.space.density))
  claim('radius', () => (out.radius = { ...incoming.radius }))
  claim('shadow', () => (out.shadow = { ...incoming.shadow }))
  claim('layout.patterns', () => (out.layout = { patterns: [...incoming.layout.patterns] }))
  claim('voice', () => (out.voice = { ...incoming.voice }))
  claim('assets.logoUrl', () => {
    out.assets.logoUrl = incoming.assets.logoUrl
    out.assets.logoOnLight = incoming.assets.logoOnLight
  })
  claim('assets.fontFaces', () => (out.assets.fontFaces = incoming.assets.fontFaces))

  return out
}

/**
 * What is missing, as data.
 *
 * Used two ways: the playground shows it so you can see what the model was left to invent, and a preset
 * fills exactly these fields and no others (so shipping a default can never overwrite a real brand).
 */
export function styleGaps(style: Style): StyleField[] {
  const gaps: StyleField[] = []
  if (!style.color.bg) gaps.push('color.bg')
  if (!style.color.text) gaps.push('color.text')
  if (!style.color.accent) gaps.push('color.accent')
  if (!style.type.display) gaps.push('type.display')
  if (!style.type.body) gaps.push('type.body')
  if (style.type.scale == null) gaps.push('type.scale')
  // Reported so the lab can see the sizes were left to the model. No preset fills it: a preset ships a
  // ratio, and the kit path derives the five roles itself (`typeScaleForKit`), which is the fill.
  if (!style.type.roles) gaps.push('type.roles')
  if (style.space.unit == null) gaps.push('space.unit')
  if (!style.space.density) gaps.push('space.density')
  if (style.radius.md == null) gaps.push('radius')
  if (!style.shadow.depth) gaps.push('shadow')
  if (!style.layout.patterns.length) gaps.push('layout.patterns')
  if (!style.voice.tone && !style.voice.guideline) gaps.push('voice')
  return gaps
}
